import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../firebase.js";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../shared/Header";
import "../../css/BluebirdGames.css";

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

const GAME = {
    id: "chicken_freezer",
    title: "I Forgot to Take the Chicken Out of the Freezer",
    description:
        "A video game made with love by the Bluebird team. Don't forget to take the chicken out of the freezer... ;)",
    platforms: ["Windows", "Linux"],
};

const COMING_SOON_GAME = {
    id: "staceys_demise",
    title: "Stacey's Demise",
    description:
        "A fun and light hearted split screen co-op game; enjoyable by both experienced and new players. Play as Stacey and her pet dog to walk the dog in time before mom gets home, or perhaps there is more to this? This is a prequel to I FORGOT TO TAKE THE CHICKEN OUT OF THE FREEZER.",
};

function BluebirdGames() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [purchased, setPurchased] = useState(false);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [downloadingPlatform, setDownloadingPlatform] = useState(null);
    const [error, setError] = useState(null);
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsLoggedIn(true);
                setCurrentUser(user);
            } else {
                setIsLoggedIn(false);
                setCurrentUser(null);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        const sessionId = searchParams.get("session_id");
        const returnedPurchased = searchParams.get("purchased");

        if (sessionId && returnedPurchased === "true") {
            verifyPurchase(sessionId);
        } else {
            fetchPurchaseStatus();
        }
    }, [currentUser]);

    const getAuthHeaders = async () => {
        const token = await currentUser.getIdToken();
        return {
            "Content-Type": "application/json",
            Authorization: token,
            "auth-token": `Bearer ${token}`,
            "requesting-user-uid": currentUser.uid,
        };
    };

    const fetchPurchaseStatus = async () => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/games/${GAME.id}/status/`, { headers });
            if (response.ok) {
                const data = await response.json();
                setPurchased(data.purchased);
            }
        } catch {
            // silently ignore — user can still attempt purchase
        } finally {
            setLoading(false);
        }
    };

    const verifyPurchase = async (sessionId) => {
        setVerifying(true);
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/games/${GAME.id}/verify/`, {
                method: "POST",
                headers,
                body: JSON.stringify({ session_id: sessionId }),
            });
            if (response.ok) {
                const data = await response.json();
                setPurchased(data.purchased);
                if (data.purchased) {
                    setPurchaseSuccess(true);
                }
            }
        } catch {
            setError("Could not confirm your purchase. Please refresh the page.");
        } finally {
            setVerifying(false);
            setLoading(false);
        }
    };

    const handleBuyNow = async () => {
        if (!isLoggedIn) {
            navigate("/login");
            return;
        }
        setCheckoutLoading(true);
        setError(null);
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/games/${GAME.id}/checkout/`, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    success_url: `${window.location.origin}/bluebird-games?purchased=true&session_id={CHECKOUT_SESSION_ID}`,
                    cancel_url: `${window.location.origin}/bluebird-games`,
                }),
            });
            const data = await response.json();
            if (response.ok && data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                setError(data.error || "Failed to start checkout. Please try again.");
            }
        } catch {
            setError("An error occurred. Please try again.");
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handleDownload = async (platform) => {
        setDownloadingPlatform(platform);
        setError(null);
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(
                `${API_BASE}/games/${GAME.id}/download/?platform=${encodeURIComponent(platform)}`,
                { headers }
            );
            let data;
            try {
                data = await response.json();
            } catch {
                setError(`Server error (${response.status}). Please try again later.`);
                return;
            }
            if (response.ok && data.download_url) {
                window.open(data.download_url, "_blank", "noopener,noreferrer");
            } else {
                setError(data.error || "Download is not available yet. Please check back soon.");
            }
        } catch (err) {
            setError(`Request failed: ${err.message}`);
        } finally {
            setDownloadingPlatform(null);
        }
    };

    const renderAction = () => {
        if (!isLoggedIn) {
            return (
                <div className="BluebirdGames__action">
                    <p className="BluebirdGames__login-prompt">
                        Please <span className="BluebirdGames__link" onClick={() => navigate("/login")}>sign in</span> to purchase this game.
                    </p>
                </div>
            );
        }

        if (loading || verifying) {
            return (
                <div className="BluebirdGames__action">
                    <p className="BluebirdGames__status-text">
                        {verifying ? "Confirming your purchase..." : "Loading..."}
                    </p>
                </div>
            );
        }

        if (purchased) {
            return (
                <div className="BluebirdGames__action">
                    {purchaseSuccess && (
                        <div className="BluebirdGames__success-banner">
                            Thank you for your purchase! A confirmation email is on its way.
                        </div>
                    )}
                    <p className="BluebirdGames__owned-label">You own this game</p>
                    <div className="BluebirdGames__download-row">
                        {GAME.platforms.map((p) => {
                            const key = p.toLowerCase();
                            const isLoading = downloadingPlatform === key;
                            return (
                                <button
                                    key={key}
                                    className="BluebirdGames__download-button"
                                    onClick={() => handleDownload(key)}
                                    disabled={downloadingPlatform !== null}
                                >
                                    {isLoading ? `Preparing ${p}...` : `Download for ${p}`}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return (
            <div className="BluebirdGames__action">
                <button
                    className="BluebirdGames__buy-button"
                    onClick={handleBuyNow}
                    disabled={checkoutLoading}
                >
                    {checkoutLoading ? "Redirecting to checkout..." : "Buy Now"}
                </button>
                <p className="BluebirdGames__platform-note">
                    Available for {GAME.platforms.join(" & ")}
                </p>
            </div>
        );
    };

    return (
        <>
            <Header />
            <div className="BluebirdGames__page">
              <div className="BluebirdGames__container">
                <div className="BluebirdGames__hero">
                    <h1 className="BluebirdGames__page-title">Bluebird Games</h1>
                    <p className="BluebirdGames__page-subtitle">Games made with love by the Bluebird team</p>
                </div>

                <div className="BluebirdGames__catalog">
                    <div className="BluebirdGames__card">
                        <div className="BluebirdGames__card-body">
                            <h2 className="BluebirdGames__game-title">{GAME.title}</h2>
                            <p className="BluebirdGames__game-description">{GAME.description}</p>
                            <div className="BluebirdGames__platforms">
                                {GAME.platforms.map((p) => (
                                    <span key={p} className="BluebirdGames__platform-badge">{p}</span>
                                ))}
                            </div>
                        </div>
                        <div className="BluebirdGames__card-footer">
                            {renderAction()}
                            {error && (
                                <p className="BluebirdGames__error">{error}</p>
                            )}
                        </div>
                    </div>

                    <div className="BluebirdGames__card BluebirdGames__card--coming-soon">
                        <div className="BluebirdGames__card-body">
                            <h2 className="BluebirdGames__game-title">{COMING_SOON_GAME.title}</h2>
                            <p className="BluebirdGames__game-description">{COMING_SOON_GAME.description}</p>
                        </div>
                        <div className="BluebirdGames__card-footer">
                            <div className="BluebirdGames__action">
                                <span className="BluebirdGames__coming-soon-label">Coming Soon</span>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            </div>
        </>
    );
}

export default BluebirdGames;
